import { motion, useInView } from 'framer-motion'
import { useRef } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { router } from '@inertiajs/react'
import { ArrowRight } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { fadeInUp, staggerContainer } from '@/lib/motion'

const schema = z.object({
  name: z.string().min(1, 'Name is required').max(255),
  email: z.string().email('Enter a valid email').max(255),
  company: z.string().max(255).optional().or(z.literal('')),
  use_case: z.string().max(2000).optional().or(z.literal('')),
})

type FormValues = z.infer<typeof schema>

interface Props {
  flashMessage?: string | null
  interestErrors?: string[] | null
}

export function InterestSection({ flashMessage, interestErrors }: Props) {
  const ref = useRef(null)
  const isInView = useInView(ref, { once: true, margin: '-100px' })

  const {
    register,
    handleSubmit,
    formState: { errors: fieldErrors, isSubmitting },
    reset,
  } = useForm<FormValues>({ resolver: zodResolver(schema) })

  function onSubmit(data: FormValues) {
    router.post(
      '/express-interest',
      { interest_submission: data },
      {
        preserveScroll: true,
        onSuccess: () => reset(),
      }
    )
  }

  return (
    <section id="interest" aria-label="Express interest" className="relative py-28 lg:py-40">
      {/* Soft aurora behind the form */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-x-0 -top-20 mx-auto h-[480px] max-w-3xl"
        style={{
          background:
            'radial-gradient(ellipse at center, hsl(var(--brand-2) / 0.16) 0%, transparent 60%)',
        }}
      />

      <div className="relative mx-auto max-w-5xl px-6 lg:px-8">
        <motion.div
          ref={ref}
          variants={staggerContainer}
          initial="hidden"
          animate={isInView ? 'visible' : 'hidden'}
          className="grid items-start gap-12 lg:grid-cols-[5fr_6fr]"
        >
          <motion.div variants={fadeInUp}>
            <div className="font-mono text-[11px] uppercase tracking-[0.22em] text-muted-foreground">
              06 / early access
            </div>
            <h2 className="mt-3 font-heading text-3xl font-bold leading-tight tracking-tight sm:text-4xl">
              Get Early Access
            </h2>
            <p className="mt-5 text-lg text-muted-foreground">
              Join our beta program and be among the first to build with KonexZero.
            </p>

            <ul className="mt-8 space-y-3 font-mono text-[12px] uppercase tracking-[0.16em] text-muted-foreground">
              {[
                'free tier — 1,000 participant-minutes / mo',
                'no card required to start',
                'rest API · openapi spec',
              ].map((line) => (
                <li key={line} className="flex items-center gap-3">
                  <span className="h-px w-6 bg-[hsl(var(--brand-2)/0.6)]" />
                  <span>{line}</span>
                </li>
              ))}
            </ul>
          </motion.div>

          <motion.div variants={fadeInUp} className="surface-card p-8 sm:p-10">
            {flashMessage && (
              <Alert className="mb-6 border-0 bg-[hsl(var(--signal-live)/0.12)] text-foreground">
                <AlertDescription>{flashMessage}</AlertDescription>
              </Alert>
            )}

            {interestErrors && interestErrors.length > 0 && (
              <Alert variant="destructive" className="mb-6">
                <AlertDescription>{interestErrors.join('. ')}</AlertDescription>
              </Alert>
            )}

            <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
              <div className="grid sm:grid-cols-2 sm:gap-4">
                <div className="space-y-1.5">
                  <Label htmlFor="interest-name" className="font-mono text-[11px] uppercase tracking-[0.16em] text-muted-foreground">
                    Name
                  </Label>
                  <Input
                    id="interest-name"
                    placeholder="Jane Doe"
                    className="h-11 rounded-lg border-foreground/10 bg-background/50"
                    {...register('name')}
                  />
                  {fieldErrors.name && (
                    <p className="text-xs text-destructive">{fieldErrors.name.message}</p>
                  )}
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="interest-email" className="font-mono text-[11px] uppercase tracking-[0.16em] text-muted-foreground">
                    Work email
                  </Label>
                  <Input
                    id="interest-email"
                    type="email"
                    placeholder="jane@acme.com"
                    className="h-11 rounded-lg border-foreground/10 bg-background/50"
                    {...register('email')}
                  />
                  {fieldErrors.email && (
                    <p className="text-xs text-destructive">{fieldErrors.email.message}</p>
                  )}
                </div>
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="interest-company" className="font-mono text-[11px] uppercase tracking-[0.16em] text-muted-foreground">
                  Company <span className="lowercase text-muted-foreground/60">(optional)</span>
                </Label>
                <Input
                  id="interest-company"
                  placeholder="Acme Inc"
                  className="h-11 rounded-lg border-foreground/10 bg-background/50"
                  {...register('company')}
                />
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="interest-use-case" className="font-mono text-[11px] uppercase tracking-[0.16em] text-muted-foreground">
                  What are you building? <span className="lowercase text-muted-foreground/60">(optional)</span>
                </Label>
                <Textarea
                  id="interest-use-case"
                  placeholder="Telehealth video calls, live tutoring, live events…"
                  rows={3}
                  className="rounded-lg border-foreground/10 bg-background/50"
                  {...register('use_case')}
                />
              </div>

              <Button
                type="submit"
                disabled={isSubmitting}
                className="group h-12 w-full rounded-lg text-sm font-semibold"
              >
                {isSubmitting ? 'Submitting…' : 'Request access'}
                <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-0.5" />
              </Button>
            </form>
          </motion.div>
        </motion.div>
      </div>
    </section>
  )
}
